import { X, Loader2 } from 'lucide-react';

interface DownloadWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  extensionName: string;
  isDownloading?: boolean;
}

export default function DownloadWarningModal({
  isOpen,
  onClose,
  onConfirm,
  extensionName,
  isDownloading = false
}: DownloadWarningModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl p-6 relative">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="space-y-4">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-1">⚠️ Important Notice | إشعار مهم</h2>
            <h3 className="text-base font-semibold text-indigo-600">
              {extensionName}
            </h3>
          </div>

          <div className="space-y-4 text-sm text-gray-600">
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="font-medium text-yellow-800 text-center">
                Please read carefully before downloading | يرجى القراءة بعناية قبل التحميل
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Installation Safety */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="mb-2">
                  <span className="font-semibold">⚡ Installation Safety:</span>
                  <br />
                  Only install extensions from our official website. Installing from other sources may compromise your security and data privacy.
                </p>
                <p className="text-right" dir="rtl">
                  <span className="font-semibold">⚡ سلامة لتثبيت:</span>
                  <br />
                  قم بتثبيت الإضافات من موقعنا الرسمي فقط. قد يؤدي التثبيت من مصادر أخرى إلى تعريض أمنك وخصوصية بياناتك للخطر.
                </p>
              </div>

              {/* Disclaimer & Troubleshooting */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="mb-2">
                  <span className="font-semibold">⚠️ Disclaimer & Troubleshooting:</span>
                  <br />
                  If you experience any issues, uninstall the extension from Extension Manager or clear browser cookies/data. We are not responsible for improper use.
                </p>
                <p className="text-right" dir="rtl">
                  <span className="font-semibold">⚠️ إخلاء المسؤولية وحل المشكلات:</span>
                  <br />
                  إذا واجهت أي مشاكل، قم بإلغاء تثبيت الإضافة من مدير الإضافات أو مسح ملفات تعريف الارتباط/البيانات. نحن غير مسؤولين عن سوء الاستخدام.
                </p>
              </div>
            </div>

            {/* Sharing Notice */}
            <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100">
              <div className="space-y-2">
                <p className="mb-2">
                  <span className="font-semibold">🔒 Extension Sharing:</span>
                  <br />
                  Please use the share button to share the extension page link. Do not share the extension file directly.
                </p>
                <p className="text-right" dir="rtl">
                  <span className="font-semibold">🔒 مشاركة الإضافة:</span>
                  <br />
                  يرجى استخدام زر المشاركة لمشاركة رابط صفحة الإضافة. لا تقم بمشاركة ملف الإضافة مباشرة.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              onClick={onClose}
              disabled={isDownloading}
              className={`px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg ${
                isDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'
              }`}
            >
              Cancel | إلغاء
            </button>
            <button
              onClick={() => {
                onConfirm();
                // Don't close modal immediately, let the download process handle it
              }}
              disabled={isDownloading}
              className={`px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg flex items-center space-x-2 ${
                isDownloading ? 'opacity-90 cursor-not-allowed' : 'hover:bg-indigo-700'
              }`}
            >
              {isDownloading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Preparing Download... | جاري التحضير...</span>
                </>
              ) : (
                <span>I Understand | أفهم</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 